import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
    ApiService,
    AuthService,
    DocumentService,
    handleClassCondition,
    PageService,
    PageType,
    PostService,
    PostType,
    SettingsService,
} from '@gallaghercp/core-frontend';
import { filter, Observable, Subscription } from 'rxjs';
import { APP_CONFIG } from '../../app.config';
import { ClientService } from '../../services/client.service';

@Component({
    selector: 'core-main',
    templateUrl: './main.component.html',
    standalone: false,
})
export class MainComponent implements OnInit, AfterViewInit, OnDestroy {
    protected router = inject(Router);
    protected auth = inject(AuthService);
    protected client = inject(ClientService);
    protected settings = inject(SettingsService);
    protected pages = inject(PageService);
    protected posts = inject(PostService);
    protected api = inject(ApiService);
    protected route = inject(ActivatedRoute);
    protected sanitizer = inject(DomSanitizer);
    protected changeDetectorRef = inject(ChangeDetectorRef);
    protected documentService = inject(DocumentService);

    containerClass?: string;
    page?: any;
    post: any;
    category?: string | boolean;
    delayedLoaded: boolean = true;
    gtm: any;

    isTopNav$?: Observable<boolean>;
    isTopNavTransparent?: boolean;
    notificationsEnabled?: boolean;
    heroImagesEnabled?: boolean;
    hasBlurEffect?: boolean;
    isChatEnabled: boolean = (APP_CONFIG as any).chatEnabled ?? false;
    isDemoMode: boolean = (APP_CONFIG as any).demoMode ?? false;

    logoUrl!: string;
    currentUrl = [APP_CONFIG.frontend, this.router.url].join('');
    today = new Date();
    subscriptions: Subscription = new Subscription();
    protected logoInvertedUrl!: string;

    ngOnInit(): void {
        this.isTopNav$ = this.settings.isTopNavigation();

        // Check authentication on initial load
        this.checkAuthAndRedirect();

        // Subscribe to router events to check authentication on every navigation
        this.subscriptions.add(
            this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
                this.checkAuthAndRedirect();
            }),
        );

        this.subscriptions.add(
            this.settings.isTransparentNavigation().subscribe((state: any) => {
                this.isTopNavTransparent = state;
            }),
        );

        this.subscriptions.add(
            this.settings.isFullWidthNavigation().subscribe((state: any) => {
                switch (state) {
                    case true:
                        this.documentService.documentElement?.style.setProperty('--site-max-width', '1600px');
                        break;
                    default:
                        this.documentService.documentElement?.style.setProperty('--site-max-width', '1320px');
                        break;
                }
            }),
        );

        this.subscriptions.add(
            this.settings.isBlurredBackground().subscribe((state: any) => {
                this.hasBlurEffect = state;
            }),
        );

        this.subscriptions.add(
            this.settings.showNotifications().subscribe((state: any) => {
                this.notificationsEnabled = state;
            }),
        );

        this.subscriptions.add(
            this.settings.showHeroImages().subscribe((state: any) => {
                this.heroImagesEnabled = state;
            }),
        );

        this.subscriptions.add(
            this.settings.getInvertedLogo().subscribe((logoUrl: any) => {
                this.logoInvertedUrl = logoUrl;
                this.updateLogoUrl();
            }),
        );

        if (APP_CONFIG.gtm) {
            this.gtm = this.sanitizer.bypassSecurityTrustResourceUrl(
                'https://www.googletagmanager.com/ns.html?id=' + APP_CONFIG.gtm,
            );
        }

        this.subscriptions.add(
            this.pages.currentPage$!.subscribe((page: PageType) => {
                if (page) {
                    this.delayedLoaded = false;
                    this.page = page as PageType;
                    this.post = false;
                    this.posts.currentPost$!.next(false);
                    this.category =
                        this.page.categoryNames && this.page.categoryNames[0]
                            ? this.page.categoryNames[0].toLowerCase().replace(new RegExp(' ', 'g'), '-')
                            : false;
                    this.changeDetectorRef.detectChanges();
                    this.setContainerClass();
                    setTimeout(() => {
                        this.delayedLoaded = true;
                        this.changeDetectorRef.detectChanges();
                    }, 300);
                }
            }),
        );

        this.subscriptions.add(
            this.posts.currentPost$!.subscribe((post: PostType) => {
                if (post) {
                    this.delayedLoaded = false;
                    this.post = post;
                    this.page = undefined;
                    this.pages.currentPage$!.next(false);
                    this.category =
                        this.post.categoryNames && this.post.categoryNames[0]
                            ? this.post.categoryNames[0].toLowerCase().replace(new RegExp(' ', 'g'), '-')
                            : false;
                    this.changeDetectorRef.detectChanges();
                    this.setContainerClass();
                    setTimeout(() => {
                        this.delayedLoaded = true;
                        this.changeDetectorRef.detectChanges();
                    }, 300);
                }
            }),
        );
    }

    getBackgroundImage(): string {
        if (this.page && this.page.meta && this.page.meta.icon) {
            return APP_CONFIG.frontend + String(this.page.meta.icon);
        } else if (this.post && this.post.data.image) {
            return APP_CONFIG.frontend + String(this.post.data.image);
        }
        return '';
    }

    updateLogoUrl(): void {
        this.logoUrl = this.logoInvertedUrl ? this.logoInvertedUrl : '';
    }

    ngAfterViewInit() {
        this.setContainerClass();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    protected setContainerClass() {
        const body: Element | null = this.documentService.querySelector('body');

        switch (this.router.url) {
            case '/':
            case '/1':
            case '/#main-content':
            case '/default':
                this.containerClass = 'container-fluid p-0';
                if (body) {
                    handleClassCondition(true, 'is-home', body as HTMLElement);
                }
                this.changeDetectorRef.detectChanges();
                break;
            default:
                this.containerClass = this.isTopNavTransparent ? 'container-fluid p-0' : 'container-fluid';
                if (body) {
                    handleClassCondition(false, 'is-home', body as HTMLElement);
                }
                this.changeDetectorRef.detectChanges();
                break;
        }
    }

    private checkAuthAndRedirect(): void {
        // Only redirect if on home page routes
        const homeRoutes = ['/', '/1', '/#main-content', '/default'];
        const currentUrl = this.router.url.split('?')[0]; // Remove query params

        if (homeRoutes.includes(currentUrl) && this.auth.isAuthenticated()) {
            this.router.navigate(['/docstore']);
        }
    }
}
